import { minBy } from "lodash-es";

import type { MappedServiceData } from "@/features/flights/components/additional-services/types";
import type { AdditionalServiceData } from "@/features/flights/types/types";
import type { Traveller } from "@/features/travellers/types/types";
import { getTravelerPassengerTypeCode } from "@/features/travellers/utils/transformTraveler";
import type {
  GetFlightAvailabilityTokenAdditionalServiceV2,
  GetFlightAvailabilityTokenOutputDataV2,
} from "@/types/codegen";
import { SUPPORTED_ADDITIONAL_SERVICES_CODES } from "@/utils/constants";

type GetAvailableServicesParams = {
  traveller: Traveller;
  travellerIndex: number | null;
  optionalServices: GetFlightAvailabilityTokenAdditionalServiceV2[] | undefined;
  mandatoryServices:
    | GetFlightAvailabilityTokenAdditionalServiceV2[]
    | undefined;
};

export const getAvailableServices = ({
  traveller,
  travellerIndex,
  optionalServices,
  mandatoryServices,
}: GetAvailableServicesParams) => {
  return [
    ...(optionalServices || []).map((service) => ({
      service,
      mandatory: false,
    })),
    ...(mandatoryServices || []).map((service) => ({
      service,
      mandatory: true,
    })),
  ].filter(
    ({ service }) =>
      SUPPORTED_ADDITIONAL_SERVICES_CODES.includes(
        service.service.categoryCode,
      ) &&
      !service.included &&
      Number(service.passengerIndex) === travellerIndex &&
      service.target === getTravelerPassengerTypeCode(traveller),
  );
};

export const getCheapestMandatoryServices = (services: MappedServiceData[]) => {
  return services.reduce((acc, { service, mandatory }) => {
    if (!mandatory) {
      return acc;
    }

    const cheapestOption = minBy(
      service.options,
      (option) => option.price.amount,
    );

    if (!cheapestOption) {
      return acc;
    }

    return [
      ...acc,
      {
        service: {
          bookingCode: service.bookingCode,
          selectedOptionCode: cheapestOption.code,
        },
        mandatory,
        categoryCode: service.service.categoryCode,
      },
    ];
  }, [] as AdditionalServiceData[]);
};

export const getSelectedServicesPrice = (
  availability: GetFlightAvailabilityTokenOutputDataV2,
): number => {
  return [
    ...(availability?.optionalServices || []),
    ...(availability?.mandatoryServices || []),
  ].reduce((acc, service) => {
    const sum = service.options
      .filter((option) => option.selected)
      .reduce((sum, option) => sum + (option.price.amount ?? 0), 0);

    return acc + sum;
  }, 0);
};

export const getNotMandatoryFreeOptionServices = (
  services: MappedServiceData[],
) => {
  return services.reduce((acc, { service, mandatory }) => {
    if (mandatory) return acc;

    const firstFreeOption = service.options.find(
      (option) => option.price.amount === 0,
    );

    if (!firstFreeOption) return acc;

    return [
      ...acc,
      {
        service: {
          bookingCode: service.bookingCode,
          selectedOptionCode: firstFreeOption.code,
        },
        mandatory,
        freeOption: true,
        categoryCode: service.service.categoryCode,
      },
    ];
  }, [] as AdditionalServiceData[]);
};
